import React, { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Modal, FormInput, FormSelect, FormTextarea, Button, SearchableSelect } from "@/components/ui";
import { INDIAN_STATES as states, REFERENCE_SOURCES as referenceSources, Verve_REQUIREMENTS } from "@/constants/globalConstants";
import { Controller } from "react-hook-form";
import { accountSchema } from "../validations/account.schema";
import { useOffices } from "@/hooks/useOffices";

export const AddAccount = ({ isOpen, onClose, onSubmit, initialData, teamMembers, user }) => {
  const { data: offices = [] } = useOffices();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    control,
    formState: { errors, isSubmitting }
  } = useForm({
    resolver: zodResolver(accountSchema),
    defaultValues: {
      firstName: "",
      lastName: "",
      phone: "",
      email: "",
      address1: "",
      address2: "",
      city: "",
      district: "",
      state: "",
      pincode: "",
      referenceType: "Direct",
      notes: "",
      requirementType: "",
      propertyType: "",
      budget: "",
      assignedTo: "",
      officeLocation: ""
    }
  });

  const selectedRefType = watch("referenceType");
  // const selectedCity = watch("city");

  const roleStr = (user?.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
  const isSalesExec = roleStr === "sales-exec" || roleStr === "sale-exec" || roleStr === "sales-executive" || roleStr === "sales";

  // Filter only sales executives who match the account's location
  const salesExecs = (teamMembers || []).filter(u => {
    const uRole = (u.role || "").toLowerCase().trim().replace(/[-_\s]+/g, "-");
    const isExec = uRole === 'sales-exec' || uRole === 'sale-exec' || uRole === 'sales-executive' || uRole === 'sales';

    // const accountLoc = (selectedCity || "").toLowerCase().trim();
    // const userLoc = (u.officeLocation || "").toLowerCase().trim();

    // Lenient match: true if either location is empty, OR if they match, OR if one contains the other
    // const matchesLocation = !userLoc ||
    //   userLoc.includes(accountLoc) ||
    //   accountLoc.includes(userLoc);

    return isExec;
  });

  useEffect(() => {
    if (isOpen) {
      if (initialData) {
        reset({
          ...initialData,
          phone: initialData.phoneNumber || initialData.phone || "",
          assignedTo: isSalesExec ? user?._id : (initialData.assignedTo?._id || initialData.assignedTo || "")
        });
      } else {
        reset({
          firstName: "", lastName: "", phone: "", email: "",
          address1: "", address2: "", city: "", district: "", state: "", pincode: "",
          referenceType: "Direct", notes: "", requirementType: "", propertyType: "", budget: "",
          assignedTo: isSalesExec ? user?._id : "",
          officeLocation: ""
        });
      }
    }
  }, [isOpen, initialData, reset, isSalesExec, user?._id]);

  const onFormSubmit = async (data) => {
    await onSubmit(data);
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={initialData ? "Edit Account Details" : "Add New Account"} size="md-xl">
      <form onSubmit={handleSubmit(onFormSubmit)} className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-3">
        <FormInput
          label="First Name"
          required
          {...register("firstName")}
          placeholder="First name"
          error={errors.firstName?.message}
          size="sm"
        />
        <FormInput
          label="Last Name"
          {...register("lastName")}
          placeholder="Last name"
          error={errors.lastName?.message}
          size="sm"
        />

        <FormInput
          label="Phone"
          required
          type="tel"
          {...register("phone")}
          placeholder="10-digit number"
          error={errors.phone?.message}
          size="sm"
        />
        <FormInput
          label="Email"
          type="email"
          {...register("email")}
          placeholder="Email address"
          error={errors.email?.message}
          size="sm"
        />

        <FormInput
          label="Address Line 1"
          {...register("address1")}
          placeholder="Street address"
          error={errors.address1?.message}
          size="sm"
        />
        <FormInput
          label="Address Line 2"
          {...register("address2")}
          placeholder="Apartment, suite, etc."
          error={errors.address2?.message}
          size="sm"
        />

        <FormInput
          label="City"
          required
          {...register("city")}
          placeholder="City"
          error={errors.city?.message}
          size="sm"
        />
        <FormInput
          label="District"
          {...register("district")}
          placeholder="District"
          error={errors.district?.message}
          size="sm"
        />

        <FormSelect
          label="State"
          required
          {...register("state")}
          placeholder="-- Select State --"
          options={states}
          error={errors.state?.message}
          size="sm"
        />
        <FormInput
          label="PIN Code"
          {...register("pincode")}
          placeholder="6-digit PIN"
          error={errors.pincode?.message}
          size="sm"
        />

        <FormSelect
          label="Office Location"
          required
          {...register("officeLocation")}
          placeholder="-- Select Office --"
          options={(Array.isArray(offices) ? offices : []).map(o => ({ value: o.name, label: o.name }))}
          error={errors.officeLocation?.message}
          size="sm"
        />

        <FormSelect
          label="Reference Source"
          {...register("referenceType")}
          placeholder="Select Reference Source"
          options={referenceSources}
          error={errors.referenceType?.message}
          size="sm"
        />

        {selectedRefType && selectedRefType !== "Direct" && (
          <div className="sm:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4 animate-in fade-in slide-in-from-top-1 duration-200">
            <FormInput
              label={selectedRefType === "Others" ? "Source Name" : "Referral Details"}
              className="sm:col-span-2"
              {...register("referenceDetails")}
              placeholder="Enter name or details..."
              error={errors.referenceDetails?.message}
              size="sm"
            />
          </div>
        )}

        <div className="sm:col-span-2">
          <FormSelect
            label={isSalesExec ? "Assigned To (You)" : "Assign Sales Executive (Optional)"}
            {...register("assignedTo")}
            disabled={isSalesExec}
            placeholder="-- No Assignment (New Account) --"
            options={salesExecs.map(m => ({ value: m._id, label: m.name }))}
            error={errors.assignedTo?.message}
            size="sm"
          />
        </div>


        <div className="grid grid-cols-2 gap-4 sm:col-span-2">
          <FormSelect
            label="Property Type"
            {...register("requirementType")}
            error={errors.requirementType?.message}
            placeholder="-- Select Type --"
            options={Verve_REQUIREMENTS}
            size="sm"
          />
          <Controller
            control={control}
            name="propertyType"
            render={({ field: { onChange, value } }) => (
              <SearchableSelect
                label="Type"
                placeholder="-- Search --"
                value={value}
                onChange={onChange}
                error={errors.propertyType?.message}
                options={[
                  "1 BHK",
                  "2 BHK",
                  "3 BHK",
                  "4 BHK / Villa",
                  "Living Room / Hall",
                  "Master Bedroom",
                  "Guest Bedroom",
                  "Kids Room"
                ]}
              />
            )}
          />
        </div>

        <FormInput
          label="Estimated Budget (₹)"
          type="number"
          {...register("budget")}
          placeholder="e.g. 250000"
          error={errors.budget?.message}
          size="sm"
        />

        <div className="sm:col-span-2">
          <FormTextarea
            label="Notes"
            rows={3}
            {...register("notes")}
            placeholder="Additional details..."
            error={errors.notes?.message}
            size="sm"
          />
        </div>

        <div className="flex gap-3 mt-6 pt-4 border-t border-slate-100 sm:col-span-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>Cancel</Button>
          <Button
            type="submit"
            variant="orange"
            className="flex-1"
            loading={isSubmitting}
            disabled={isSubmitting}
          >
            {initialData ? "Update Account" : "Add Account"}
          </Button>
        </div>
      </form>
    </Modal>
  );
};
